import { useState, useEffect, useRef } from 'react';
import SettingsPageShell from './SettingsPageShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { retailerSettings } from '../../utils/api';
import { toast } from 'sonner';
import { Save, Upload, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const emptyHours = () => DAYS.reduce((acc, d) => ({ ...acc, [d]: { open: '09:00', close: '21:00', closed: false } }), {});

export default function RetailerStoreInfo({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const logoInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    retailerSettings.getStoreInfo().then((res) => {
      const d = res.data;
      setData({
        ...d,
        weekly_hours: Object.keys(d.weekly_hours || {}).length ? d.weekly_hours : emptyHours(),
        holiday_hours: d.holiday_hours || [],
      });
    }).catch(() => toast.error('Failed to load store info'));
  }, []);

  const setField = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const setHours = (day, k, v) => setData((p) => ({ ...p, weekly_hours: { ...p.weekly_hours, [day]: { ...p.weekly_hours[day], [k]: v } } }));
  const addHoliday = () => setData((p) => ({ ...p, holiday_hours: [...p.holiday_hours, { date: '', label: '', closed: true, open: '', close: '' }] }));
  const updateHoliday = (i, k, v) => setData((p) => ({ ...p, holiday_hours: p.holiday_hours.map((h, idx) => idx === i ? { ...h, [k]: v } : h) }));
  const removeHoliday = (i) => setData((p) => ({ ...p, holiday_hours: p.holiday_hours.filter((_, idx) => idx !== i) }));

  const uploadFile = async (file, kind) => {
    if (!file) return;
    if (kind === 'logo') setUploadingLogo(true); else setUploadingImage(true);
    try {
      if (kind === 'logo') {
        const res = await retailerSettings.uploadStoreLogo(file);
        setField('store_logo_url', res.data.store_logo_url);
        toast.success('Logo uploaded');
      } else {
        const res = await retailerSettings.uploadStoreImage(file);
        setField('store_image_url', res.data.store_image_url);
        toast.success('Store image uploaded');
      }
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message || 'unknown error'}`);
    } finally {
      setUploadingLogo(false);
      setUploadingImage(false);
    }
  };

  const save = async () => {
    if (!data.store_logo_url) {
      toast.error('Store Logo is required');
      return;
    }
    if (!data.store_image_url) {
      toast.error('Store Image is required to uniquely identify your store location.');
      return;
    }
    setSaving(true);
    try {
      const { store_logo_url, store_image_url, ...rest } = data;
      await retailerSettings.updateStoreInfo(rest);
      toast.success('Store info saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <SettingsPageShell user={user} onLogout={onLogout} title="Store Info"><div className="text-gray-500">Loading…</div></SettingsPageShell>;

  return (
    <SettingsPageShell
      user={user}
      onLogout={onLogout}
      title="Store Info"
      subtitle="Identify your specific store location"
      actions={
        <Button onClick={save} disabled={saving} data-testid="store-info-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      }
    >
      {/* Basic Details */}
      <Card className="border-0 shadow-md">
        <CardHeader><CardTitle>Basic Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="store-name">Store Name <span className="text-red-500">*</span></Label>
            <Input id="store-name" data-testid="store-name-input" value={data.store_name} onChange={(e) => setField('store_name', e.target.value)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Store Address <span className="text-red-500">*</span></Label>
              <Input data-testid="store-address-input" value={data.store_address} onChange={(e) => setField('store_address', e.target.value)} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={data.store_city || ''} onChange={(e) => setField('store_city', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>State</Label><Input value={data.store_state || ''} onChange={(e) => setField('store_state', e.target.value)} /></div>
              <div><Label>ZIP</Label><Input value={data.store_zip || ''} onChange={(e) => setField('store_zip', e.target.value)} /></div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Contact Email <span className="text-red-500">*</span></Label>
              <Input type="email" data-testid="contact-email-input" value={data.contact_email} onChange={(e) => setField('contact_email', e.target.value)} />
            </div>
            <div>
              <Label>Contact Phone <span className="text-red-500">*</span></Label>
              <Input data-testid="contact-phone-input" value={data.contact_phone} onChange={(e) => setField('contact_phone', e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Branding</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Both Logo and Store Image are required. The Store Image visually identifies <strong>your specific location</strong>,
            critical for national chains where multiple stores share the same logo.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo */}
          <div className="grid md:grid-cols-[140px_1fr] gap-4 items-start">
            <div className="relative">
              {data.store_logo_url ? (
                <img src={data.store_logo_url} alt="Logo" className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200" />
              ) : (
                <div className="w-32 h-32 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <Label>Store Logo <span className="text-red-500">*</span></Label>
              <p className="text-xs text-gray-500 mb-2">Square image (1:1), min 512×512. JPEG, PNG, WebP. Max 5 MB.</p>
              <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={(e) => uploadFile(e.target.files[0], 'logo')} />
              <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                <Upload className="w-4 h-4 mr-2" /> {uploadingLogo ? 'Uploading…' : (data.store_logo_url ? 'Replace Logo' : 'Upload Logo')}
              </Button>
            </div>
          </div>

          {/* Store Image (hero) */}
          <div className="grid md:grid-cols-[280px_1fr] gap-4 items-start">
            <div className="relative">
              {data.store_image_url ? (
                <img src={data.store_image_url} alt="Store" className="w-full aspect-video rounded-lg object-cover border-2 border-gray-200" />
              ) : (
                <div className="w-full aspect-video rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <ImageIcon className="w-10 h-10 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <Label>Store Image <span className="text-red-500">*</span></Label>
              <p className="text-xs text-gray-500 mb-2">Hero / cover (16:9), min 1280×720. Used on the consumer storefront.</p>
              <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={(e) => uploadFile(e.target.files[0], 'image')} />
              <Button variant="outline" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} data-testid="upload-store-image-btn">
                <Upload className="w-4 h-4 mr-2" /> {uploadingImage ? 'Uploading…' : (data.store_image_url ? 'Replace Image' : 'Upload Store Image')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hours */}
      <Card className="border-0 shadow-md">
        <CardHeader><CardTitle>Hours</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {DAYS.map((day) => (
              <div key={day} className="grid grid-cols-[120px_1fr_1fr_auto] gap-3 items-center">
                <div className="capitalize font-medium text-gray-700">{day}</div>
                <Input type="time" value={data.weekly_hours[day]?.open || '09:00'} onChange={(e) => setHours(day, 'open', e.target.value)} disabled={data.weekly_hours[day]?.closed} data-testid={`hours-${day}-open`} />
                <Input type="time" value={data.weekly_hours[day]?.close || '21:00'} onChange={(e) => setHours(day, 'close', e.target.value)} disabled={data.weekly_hours[day]?.closed} data-testid={`hours-${day}-close`} />
                <label className="text-sm text-gray-600 flex items-center gap-2">
                  <input type="checkbox" checked={data.weekly_hours[day]?.closed || false} onChange={(e) => setHours(day, 'closed', e.target.checked)} data-testid={`hours-${day}-closed`} /> Closed
                </label>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold">Holiday Hours</Label>
              <Button variant="outline" size="sm" onClick={addHoliday} data-testid="add-holiday-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Holiday
              </Button>
            </div>
            {data.holiday_hours.length === 0 && <p className="text-sm text-gray-400">No special holiday hours set.</p>}
            <div className="space-y-2">
              {data.holiday_hours.map((h, i) => (
                <div key={i} className="grid grid-cols-[1fr_2fr_auto_auto] gap-2 items-center bg-gray-50 p-2 rounded">
                  <Input type="date" value={h.date} onChange={(e) => updateHoliday(i, 'date', e.target.value)} />
                  <Input placeholder="Christmas Eve" value={h.label} onChange={(e) => updateHoliday(i, 'label', e.target.value)} />
                  <label className="text-xs text-gray-600 flex items-center gap-1"><input type="checkbox" checked={h.closed} onChange={(e) => updateHoliday(i, 'closed', e.target.checked)} /> Closed</label>
                  <Button variant="ghost" size="sm" onClick={() => removeHoliday(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      <Card className="border-0 shadow-md">
        <CardHeader><CardTitle>Description (optional)</CardTitle></CardHeader>
        <CardContent>
          <Textarea rows={4} placeholder="Tell customers what makes your store unique" value={data.description} onChange={(e) => setField('description', e.target.value)} data-testid="store-description-input" />
        </CardContent>
      </Card>
    </SettingsPageShell>
  );
}
